import { ElementType, FC, useEffect, useState } from 'react';
import React from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { profileSubmenu } from '../../profile/ProfileData';
import { AccessControl, AssetCommon } from './_models';
import { Client, ClientContact, ClientContactConsent, Provider } from '../../../helpers/models';
import { getClientById } from '../../apps/clients/core/_requests';
import { useAuth } from '../../../../app/modules/auth';
type Props = {
  show?: boolean;
  client?: Client;
  asset?: AssetCommon;
  title?: string;
  description?: string;
  provider?: Provider;
  onCancel?: () => void;
  onSubmit?: (updates: AccessControl) => void;
  // When set, render a "Save Only" button that dismisses the dialog without
  // sharing. Used by the Save & Share flow so users who opened the dialog by
  // mistake can keep the save without picking recipients.
  onSaveOnly?: () => void;
  saveOnlyLabel?: string;
  // Shown under the recipients when the client has guardians (e.g. a reminder
  // that guardians only see what they are ticked on).
  guardianHint?: string;
};

const AssetShareDialog: FC<Props> = (props) => {
  const [show, setShow] = useState(props.show);
  const { currentProvider } = useAuth();

  const assetToAccess = (asset?: AssetCommon) => {
    return {
      client_access: asset?.client_access || false,
      guardians_access: asset?.guardians_access || [],
      contacts_access: asset?.contacts_access || [],
      public_access: false, //asset?.public_access,
    };
  };

  const [access, setAccess] = useState(assetToAccess(props.asset));
  const [client, setClient] = useState(props.client);

  useEffect(() => {
    if (props.client?.uuid) {
      getClientById(props.client.uuid).then((c) => {
        if (c) {
          setClient(c);
        }
      });
    }
  }, [props.client]);

  useEffect(() => {
    setShow(props.show);
  }, [props.show]);

  // useEffect(() => {
  //   setAccess(assetToAccess(props.asset));
  // }, [props.asset]);

  const handleCancel = () => {
    setAccess(assetToAccess(props.asset));
    props.onCancel?.();
    setShow(false);
  };

  const handleSubmit = () => {
    props.onSubmit?.(access);
    setShow(false);
  };

  const hasConsent = (cc: ClientContact) => {
    return cc.consents?.some(
      (c: ClientContactConsent) =>
        (c.provider?.uuid == props.provider?.uuid ||
          (c.allow_org && c.provider?.parent_org?.uuid == props.provider?.parent_org?.uuid) ||
          (c.allow_org && c.provider?.parent_org?.uuid == props.provider?.uuid)) &&
        c.consent_date
    );
  };

  return (
    <>
      <Modal show={show} onHide={handleCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>{props.title ? props.title : 'Sharing'}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <label className="fw-bold fs-6 mb-1 mt-5">{props.description ? props.description : 'Share with:'}</label>
          <div className="row">
            <Form.Check
              label={`${client?.name} ${!client?.email ? '(no email)' : ''}`}
              name="share-client"
              type="checkbox"
              id="share-client"
              className="ms-3 me-4 mb-0 px-10 py-3 col-12"
              checked={access?.client_access || false}
              onChange={(e) => {
                setAccess({ ...access, client_access: e.target.checked });
              }}
              disabled={!client?.email}
            />
          </div>
          {client?.guardians?.map((g) => (
            <div className="row" key={g.uuid}>
              <Form.Check
                key={g.uuid}
                label={`${g.name} ${!g?.email ? '(no email)' : ''}`}
                name="share-guardians"
                type="checkbox"
                id={`share-guardians-${g.uuid}`}
                className="ms-3 me-4 mb-0 px-10 py-3 col-12"
                checked={access?.guardians_access.some((ga) => ga.uuid === g.uuid)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setAccess({ ...access, guardians_access: [...access.guardians_access, g] });
                  } else {
                    setAccess({
                      ...access,
                      guardians_access: access.guardians_access.filter((ga) => ga.uuid !== g.uuid),
                    });
                  }
                }}
                disabled={!g?.email}
              />
            </div>
          ))}
          {props.guardianHint && !!client?.guardians?.length && (
            <div className="text-muted fs-7 mt-2" data-testid="guardian-hint">
              <i className="fas fa-info-circle me-1"></i>
              {props.guardianHint}
            </div>
          )}
          {client?.contacts?.map((cc) => (
            <div className="row" key={cc.uuid}>
              <Form.Check
                key={cc.uuid}
                label={`${cc.contact?.name} ${cc.role ? '(' + cc.role + ') ' : ''}${
                  !cc.contact?.email ? '(no email)' : !hasConsent(cc) ? '(no release of information)' : ''
                }`}
                name="share-guardians"
                type="checkbox"
                id={`share-guardians-${cc.uuid}`}
                className="ms-3 me-4 mb-0 px-10 py-3 col-12"
                checked={access?.contacts_access.some((cca) => cca.uuid === cc.uuid)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setAccess({ ...access, contacts_access: [...access.contacts_access, cc] });
                  } else {
                    setAccess({
                      ...access,
                      contacts_access: access.contacts_access.filter((cca) => cca.uuid !== cc.uuid),
                    });
                  }
                }}
                disabled={!cc?.contact?.email || !hasConsent(cc)}
              />
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline btn-outline-primary me-3" type="button" onClick={handleCancel}>
            Cancel
          </button>

          {props.onSaveOnly && (
            <button
              className="btn btn-outline btn-outline-primary me-3"
              type="button"
              onClick={() => {
                props.onSaveOnly?.();
                setShow(false);
              }}
            >
              {props.saveOnlyLabel || 'Save Only'}
            </button>
          )}

          <button className="btn btn-primary" type="button" onClick={handleSubmit}>
            Submit
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export { AssetShareDialog };
